<template>
  <div v-if="state.open && state.opts" @keydown.esc="cancel" tabindex="-1">
    <div
        @click.stop="cancel"
        class="bg-black h-full w-full fixed inset-0 z-[60] opacity-30"
    ></div>
    <div class="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div class="bg-white rounded-md shadow-soft3xl w-full max-w-[480px] max-h-[85vh] flex flex-col">
        <div class="px-6 xl:px-10 pt-7 pb-2 text-center">
          <p class="text-lg lg:text-xl font-semibold text-gray-600">{{ state.opts.title }}</p>
        </div>

        <div class="px-6 xl:px-10 pb-6 overflow-y-auto">
          <div class="mt-2 text-sm text-gray-500 mng-message" v-html="state.opts.message"></div>

          <Form :validation-schema="schema" @submit="onSubmit" class="mt-5">
            <InputNumber
                name="ticket_id"
                :title="ticketRequired ? 'Ticket ID*' : 'Ticket ID'"
                v-model="form.ticket_id"
                placeholder="e.g. 12345"
                :min-value="0"
            />
            <InputTextarea
                class="mt-4"
                name="extra_info"
                title="Extra info"
                v-model="form.extra_info"
                placeholder="Optional notes"
            />

            <div class="flex justify-center gap-3 mt-8">
              <MainButton type="primary-outline" :button-type="'button'" @action="cancel">Cancel</MainButton>
              <MainButton type="primary" :button-type="'submit'">{{ state.opts.confirmButtonText }}</MainButton>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect } from "vue";
import { Form } from "vee-validate";
import * as yup from "yup";
import MainButton from "@/components/buttons/MainButton.vue";
import InputNumber from "@/components/form/InputNumber.vue";
import InputTextarea from "@/components/form/InputTextarea.vue";
import { manageActionState as state, resolveManageAction } from "@/helpers/ManageActionPrompt.ts";

const form = ref<{ ticket_id: number | null; extra_info: string | null }>({
  ticket_id: null,
  extra_info: null,
});

const ticketRequired = computed(() => state.opts?.action === "approve" || state.opts?.action === "modify");

const schema = computed(() =>
  yup.object().shape({
    ticket_id: yup.number().nullable()
      .transform((value, original) => (original === "" || original === null ? null : value))
      .test({
        name: 'checkRequired',
        test: function (value) {
          if (ticketRequired.value && !value) {
            throw this.createError({
              message: `Ticket ID is required`,
            });
          }

          return true;
        },
        message: 'Ticket ID is required'
      })
      .integer("Ticket ID must be a whole number")
      .min(1, "Ticket ID must be a valid number"),
    extra_info: yup.string().nullable(),
  }),
);

const onSubmit = (values: Record<string, any>) => {
  const extra = (values.extra_info ?? "").toString().trim();
  resolveManageAction({
    isConfirmed: true,
    ticket_id: values.ticket_id ?? null,
    extra_info: extra === "" ? null : extra,
  });
};

const cancel = () => {
  resolveManageAction({ isConfirmed: false, ticket_id: null, extra_info: null });
};

watch(() => state.open, (open) => {
  if (open === true) {
    form.value = { ticket_id: null, extra_info: null };
  }
});

watchEffect(() => {
  if (state.open === true) {
    document.body.classList.add("no-scroll");
  } else {
    document.body.classList.remove("no-scroll");
  }
});
</script>

<style scoped>
.mng-message :deep(p) {
  margin-bottom: 8px;
}
.mng-message :deep(p:last-child) {
  margin-bottom: 0;
}
</style>
